'use client';
import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Card, Spinner } from 'react-bootstrap';
import Button from 'components/base/Button';
import Badge from 'components/base/Badge';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faClock,
  faMapPin,
  faPoundSign,
  faUserMd,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import { getShiftById } from 'features/shift/shiftApi';
import { bookShift, getMyBookings } from 'features/booking/bookingApi';

const ShiftDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [shift, setShift] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(false);

  useEffect(() => {
    const fetchShift = async () => {
      try {
        const shiftData = await getShiftById(id!);
        const myBookings = await getMyBookings();
        const isBooked = myBookings?.some(
          (b: any) => b.shift_id === parseInt(id!)
        );
        setShift({ ...shiftData, is_booked: isBooked });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchShift();
  }, [id, location.key]);

  const handleBook = async () => {
    try {
      setBooking(true);
      await bookShift(shift.id);

      // update UI instantly
      setShift((prev: any) => ({ ...prev, is_booked: true }));
    } catch (err: any) {
      alert(err.message);
    } finally {
      setBooking(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-6">
        <Spinner />
      </div>
    );
  }

  if (!shift) {
    return <p className="text-center">Shift not found</p>;
  }

  return (
    <div className="container py-4">
      {/* 🔹 Back Button */}
      <div className="mb-3">
        <Button color="secondary" size="sm" onClick={() => navigate(-1)}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Back
        </Button>
      </div>

      {/* 🔹 Header */}
      <h4 className="mb-4 fw-semibold">Shift Details</h4>

      {/* 🔹 Main Card */}
      <Card className="border border-translucent shadow-sm">
        <Card.Body className="p-4">
          {/* Top Section */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h5 className="mb-1 fw-semibold">
                {new Date(shift.date).toLocaleDateString('en-GB', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long'
                })}
              </h5>
              <small className="text-body-tertiary">{shift.locum_role}</small>
            </div>

            <Badge variant="phoenix" bg="success">
              Open
            </Badge>
          </div>

          {/* Info */}
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <small className="text-body-tertiary">
                <FontAwesomeIcon icon={faClock} className="me-2" />
                {shift.start_time} - {shift.end_time}
              </small>
            </div>

            <div className="col-md-6">
              <small className="text-body-tertiary">
                <FontAwesomeIcon icon={faMapPin} className="me-2" />
                {shift.location}
              </small>
            </div>

            <div className="col-md-6">
              <small className="text-body-tertiary">
                <FontAwesomeIcon icon={faUserMd} className="me-2" />
                {shift.locum_role}
              </small>
            </div>

            <div className="col-md-6">
              <small className="text-body-tertiary">
                <FontAwesomeIcon icon={faPoundSign} className="me-2" />
                <strong>{shift.pay_rate}</strong> / hour
              </small>
            </div>
          </div>

          {/* Description */}
          {shift.summary && (
            <div className="mb-4">
              <h6 className="fw-semibold">Shift Description</h6>
              <p className="text-body-tertiary mb-0">{shift.summary}</p>
            </div>
          )}

          {/* 🔹 CTA */}
          <div className="d-flex justify-content-end">
            {shift.is_booked ? (
              <Button color="secondary" size="lg" disabled>
                ✔ Booked
              </Button>
            ) : (
              <Button
                color="primary"
                size="lg"
                disabled={booking}
                onClick={handleBook}
              >
                {booking ? 'Booking...' : 'Book Now'}
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ShiftDetailsPage;
